import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Bot, X } from 'lucide-react';
import ChatWindow from './ChatWindow';
import { collectPageContext } from './pageContextCollector';
import { shouldShowProactiveBubble } from './proactiveBubblePolicy';
import { BTN } from '../../styles/tokens';
import { getMockResponse } from './mockService';
import { sendAssistantMessage, updateSettings } from '../../services/api';
import { useAppContext } from '../../contexts/AppContext';
import { useGenContext } from '../../contexts/GenContext';
import type { AssistantChatMessage, ApiAction } from './types';
import { PAGE_LABELS } from './types';
import type { GenConfig } from '../../types';

// 主動氣泡的訊息集合（條件 A：生成後 / 條件 B：閒置）
const PROACTIVE_MESSAGES_AFTER_GEN = [
  '剛才生成的滿意嗎？有想法可以回饋給我們 😊',
  '有任何建議，都可以點回饋告訴我！',
];

const PROACTIVE_MESSAGES_IDLE = [
  '有什麼用起來卡卡的嗎？可以告訴我 👂',
  '功能好不好用？有建議的話跟我說一聲吧 😊',
  '有什麼想法或建議，隨時可以跟我說！',
];

// 從陣列隨機取一個元素
const pickRandom = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

// 主動氣泡冷卻時間（ms）
const BUBBLE_COOLDOWN_MS = 30 * 60 * 1000; // 30 分鐘
// 閒置判定時間（ms）
const IDLE_THRESHOLD_MS = 2 * 60 * 1000; // 2 分鐘
// 氣泡自動消失時間（ms）
const BUBBLE_AUTO_HIDE_MS = 6000;

interface FloatingAssistantProps {
  isCollapsed: boolean;
}

const FloatingAssistant: React.FC<FloatingAssistantProps> = ({ isCollapsed }) => {
  const { organizationName, organizationType, name, title, setOrganizationName, setOrganizationType, setName, setTitle } = useAppContext();
  const { activeTab, setActiveTab, genStep, genConfig, setGenConfig, setGenStep } = useGenContext();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<AssistantChatMessage[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [openInFeedbackMode, setOpenInFeedbackMode] = useState(false);

  // 主動氣泡狀態
  const [bubbleText, setBubbleText] = useState<string | null>(null);
  const [bubbleOpensFeedback, setBubbleOpensFeedback] = useState(false);

  const sessionIdRef = useRef<string | undefined>(undefined);
  const lastBubbleTimeRef = useRef<number>(0);
  const bubbleHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentPageLabel = PAGE_LABELS[activeTab as keyof typeof PAGE_LABELS] ?? activeTab;

  // ── 氣泡工具函式 ────────────────────────────────────────────────────────────

  // 用 ref 存最新的 isOpen，讓事件 handler 不需每次重新綁定
  const isOpenRef = useRef(isOpen);
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);

  const showBubble = useCallback((text: string, opensFeedback: boolean) => {
    const now = Date.now();
    if (isOpenRef.current || now - lastBubbleTimeRef.current < BUBBLE_COOLDOWN_MS) return;

    lastBubbleTimeRef.current = now;
    setBubbleText(text);
    setBubbleOpensFeedback(opensFeedback);

    if (bubbleHideTimerRef.current) clearTimeout(bubbleHideTimerRef.current);
    bubbleHideTimerRef.current = setTimeout(() => setBubbleText(null), BUBBLE_AUTO_HIDE_MS);
  }, []);

  // ── viewport 寬度：主動氣泡的顯示規則依此判定 ──────────────────────────────
  // 用 ref 保存，讓觸發當下（含閒置計時器到期時）取到的是最新寬度，
  // 手機直向／橫向切換後行為會跟著更新。
  const viewportWidthRef = useRef<number>(window.innerWidth);
  useEffect(() => {
    const handleResize = () => { viewportWidthRef.current = window.innerWidth; };
    handleResize(); // 掛載時先同步一次
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const dismissBubble = () => {
    setBubbleText(null);
    if (bubbleHideTimerRef.current) clearTimeout(bubbleHideTimerRef.current);
  };

  // ── 條件 A：監聽生成完成事件（由各生成頁面 dispatch）────────────────────────

  useEffect(() => {
    const handler = () => {
      if (!shouldShowProactiveBubble(viewportWidthRef.current, 'generation-complete')) return;
      showBubble(pickRandom(PROACTIVE_MESSAGES_AFTER_GEN), true);
    };
    window.addEventListener('jodoni:generation:complete', handler);
    return () => window.removeEventListener('jodoni:generation:complete', handler);
  }, [showBubble]);

  // ── 條件 B：閒置 2 分鐘後顯示氣泡 ──────────────────────────────────────────

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      if (!shouldShowProactiveBubble(viewportWidthRef.current, 'idle')) return;
      showBubble(pickRandom(PROACTIVE_MESSAGES_IDLE), true);
    }, IDLE_THRESHOLD_MS);
  }, [showBubble]);

  useEffect(() => {
    // 監聽使用者互動，重置閒置計時器
    const events = ['click', 'keydown', 'pointermove', 'scroll'];
    events.forEach(e => window.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer(); // 初始化

    return () => {
      events.forEach(e => window.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (bubbleHideTimerRef.current) clearTimeout(bubbleHideTimerRef.current);
    };
  }, [resetIdleTimer]);

  // ── 小幫手打開時清除氣泡 ────────────────────────────────────────────────────

  useEffect(() => {
    if (isOpen) dismissBubble();
  }, [isOpen]);

  // ── 對話邏輯（原有邏輯不變） ────────────────────────────────────────────────

  const executeSingleAction = (action: ApiAction): string => {
    switch (action.type) {
      case 'navigate':
        if (action.tab) setActiveTab(action.tab);
        break;
      case 'set-gen-config':
        if (action.payload) setGenConfig(prev => ({ ...prev, ...(action.payload as Partial<GenConfig>) }));
        break;
      case 'set-gen-step':
        if (action.step !== undefined) setGenStep(action.step as 1 | 2 | 3 | 4);
        break;
      case 'set-global-setting':
        if (action.payload) {
          const p = action.payload as {
            organizationName?: string | null;
            organizationType?: '商家' | '里';
            name?: string;
            title?: string | null;
          };
          if (p.organizationName !== undefined) setOrganizationName(p.organizationName ?? '');
          if (p.organizationType !== undefined) setOrganizationType(p.organizationType);
          if (p.name !== undefined) setName(p.name);
          if (p.title !== undefined) setTitle(p.title);
          // 同步寫回後端（fire-and-forget）。
          // PUT /api/db/settings 支援 partial update（!== undefined 才寫），
          // 直接把 AI 提供的 payload 原樣送出即可，不必額外組欄位。
          updateSettings(p).catch(err => {
            console.error('[FloatingAssistant] 同步設定失敗:', err);
          });
        }
        break;
      case 'dispatch-event':
        if (action.eventName) {
          window.dispatchEvent(new CustomEvent(action.eventName, { detail: action.detail ?? {} }));
        }
        break;
      case 'hint':
        break;
    }
    return action.type === 'hint' ? `提示：${action.message ?? ''}` : action.description;
  };

  const executeActions = (actions: ApiAction[]): { executedLabels: string[]; pendingActions: ApiAction[] } => {
    const executedLabels: string[] = [];
    const pendingActions: ApiAction[] = [];

    for (const action of actions) {
      if (action.autoExecute !== false) {
        executedLabels.push(executeSingleAction(action));
      } else {
        pendingActions.push(action);
      }
    }

    return { executedLabels, pendingActions };
  };

  const handleExecutePendingAction = (msgId: string, action: ApiAction) => {
    executeSingleAction(action);
    setMessages(prev => prev.map(msg => {
      if (msg.id !== msgId) return msg;
      const remaining = (msg.pendingActions ?? []).filter(a => a !== action);
      return {
        ...msg,
        pendingActions: remaining.length > 0 ? remaining : undefined,
        executedLabels: [...(msg.executedLabels ?? []), action.description],
      };
    }));
  };

  const handleSend = async (text: string) => {
    const userMsg: AssistantChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);
    setIsThinking(true);

    try {
      const context = collectPageContext({ activeTab, genStep, genConfig, organizationName, organizationType, name, title });

      let responseText: string;
      let actions: ApiAction[];

      try {
        const apiResponse = await sendAssistantMessage({
          sessionId: sessionIdRef.current,
          message: text,
          pageContext: context,
        });
        sessionIdRef.current = apiResponse.sessionId;
        responseText = apiResponse.text;
        actions = apiResponse.actions as ApiAction[];
      } catch (apiErr) {
        console.warn('[FloatingAssistant] API failed, using mock:', apiErr);
        const mockResponse = await getMockResponse(text, context);
        responseText = mockResponse.text;
        actions = mockResponse.actions as ApiAction[];
      }

      const { executedLabels, pendingActions } = executeActions(actions);

      const assistantMsg: AssistantChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: responseText,
        executedLabels: executedLabels.length > 0 ? executedLabels : undefined,
        pendingActions: pendingActions.length > 0 ? pendingActions : undefined,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMsg]);

      if (!isOpen) setHasUnread(true);
    } catch (err) {
      console.error('[FloatingAssistant] Error:', err);
      setMessages(prev => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: '抱歉，發生了錯誤，請稍後再試。',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsThinking(false);
    }
  };

  const handleOpen = (feedbackMode = false) => {
    setOpenInFeedbackMode(feedbackMode);
    setIsOpen(true);
    setHasUnread(false);
  };

  return (
    <>
      {/* ChatWindow 與氣泡透過 Portal 掛到 body，完全脫離 sidebar 的 containing block */}
      {createPortal(
        <>
          <ChatWindow
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            messages={messages}
            isThinking={isThinking}
            onSend={handleSend}
            onExecutePendingAction={handleExecutePendingAction}
            currentPageLabel={currentPageLabel}
            openInFeedbackMode={openInFeedbackMode}
            positionStyle={{
              bottom: '1rem',
              left: isCollapsed ? '4.5rem' : '17rem',
            }}
          />

          {/* 主動氣泡：固定在左下角小幫手按鈕上方 */}
          {bubbleText && !isOpen && (
            <div
              className="fixed z-40 bottom-20 left-4 max-w-[220px] animate-in slide-in-from-bottom-3 fade-in duration-300"
              style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))' }}
            >
              {/* 氣泡本體 */}
              <div
                className="relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/60 rounded-2xl rounded-bl-sm px-4 py-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/80 transition-colors"
                onClick={() => {
                  dismissBubble();
                  handleOpen(bubbleOpensFeedback);
                }}
              >
                <p className="text-sm text-slate-700 dark:text-slate-200 leading-snug pr-5">{bubbleText}</p>
                <button
                  onClick={e => {
                    e.stopPropagation();
                    dismissBubble();
                  }}
                  className="absolute top-2 right-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                >
                  <X size={12} />
                </button>
              </div>
              {/* 指向下方小幫手的小三角 */}
              <div className="absolute -bottom-2 left-4 w-4 h-2 overflow-hidden">
                <div className="w-3 h-3 bg-white dark:bg-slate-800 border-b border-l border-slate-200 dark:border-slate-700/60 rotate-45 translate-x-0.5 -translate-y-1.5" />
              </div>
            </div>
          )}
        </>,
        document.body
      )}

      {isCollapsed ? (
        /* 摺疊時：固定在左下角的圓球 */
        <button
          onClick={isOpen ? () => setIsOpen(false) : () => handleOpen(false)}
          className={`fixed bottom-4 left-2 z-50 w-12 h-12 rounded-full ${BTN.primary} active:scale-95 shadow-lg shadow-blue-900/40 flex items-center justify-center duration-150`}
          title="AI 小幫手"
        >
          {isOpen ? <X size={20} /> : <Bot size={20} />}
          {hasUnread && !isOpen && (
            <span className="absolute top-0.5 right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-white animate-pulse" />
          )}
        </button>
      ) : (
        /* 展開時：Sidebar 底部的列表項目 */
        <div className="px-4 pb-2">
          <button
            onClick={isOpen ? () => setIsOpen(false) : () => handleOpen(false)}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all focus:outline-none ${
              isOpen
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100'
            }`}
            title="AI 小幫手"
          >
            <div className="relative shrink-0">
              <Bot size={20} />
              {hasUnread && !isOpen && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-white animate-pulse" />
              )}
            </div>
            <span className="font-medium">AI 小幫手</span>
          </button>
        </div>
      )}
    </>
  );
};

export default FloatingAssistant;
